import { useState } from 'react';
import type Animated from 'react-native-reanimated';
import {
  useAnimatedReaction,
  useAnimatedRef,
  useAnimatedScrollHandler,
  useSharedValue,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';

export interface UseCalendarAnimationsProps {
  months: Date[];
  monthsBehind: number;
  onMonthChange?: (month: Date) => void;
  /**
   * `getCalendarLayout`'s `monthStride`: the scroll distance from one month to
   * the next. 0 until the list has measured itself.
   */
  monthStride: number;
}

export interface UseCalendarAnimationsReturn {
  scrollViewRef: ReturnType<typeof useAnimatedRef<Animated.FlatList<Date>>>;
  scrolledMonthIndex: number;
  scrollHandler: ReturnType<typeof useAnimatedScrollHandler>;
  /**
   * Set the displayed month to `index` immediately (title + onMonthChange) and
   * lock the reaction to it until the scroll arrives. Used by the prev/next
   * arrows so the title reflects the destination during the scroll animation.
   */
  commitMonthIndex: (index: number) => void;
}

export const useCalendarAnimations = ({
  months,
  monthsBehind,
  onMonthChange,
  monthStride,
}: UseCalendarAnimationsProps): UseCalendarAnimationsReturn => {
  const [scrolledMonthIndex, setScrolledMonthIndex] = useState(monthsBehind);

  const scrollViewRef = useAnimatedRef<Animated.FlatList<Date>>();
  // Live horizontal scroll offset (px), updated by the scroll handler.
  const scrollOffset = useSharedValue(0);
  const previousMonthIndex = useSharedValue(monthsBehind);
  // While set to an index (>= 0), the scroll reaction does not derive the month
  // from the live offset — it waits until the offset reaches that index. This
  // keeps the title fixed on a known target during programmatic scrolls (the
  // initial positioning on `monthsBehind`, and arrow presses), where the live
  // offset passes through intermediate values. -1 means "follow the offset".
  const programmaticTargetIndex = useSharedValue(monthsBehind);

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: (event) => {
      /* istanbul ignore next - worklet code runs on UI thread */
      scrollOffset.value = event.contentOffset.x;
    },
    /* istanbul ignore next - worklet code runs on UI thread */
    onBeginDrag: () => {
      // A manual swipe makes the month follow the live offset again.
      programmaticTargetIndex.value = -1;
    },
  });

  const handleMonthChange = (monthIndex: number) => {
    setScrolledMonthIndex(monthIndex);

    const newMonth = months?.[monthIndex];

    if (newMonth && onMonthChange) {
      onMonthChange(newMonth);
    }
  };

  // Set the displayed month directly (title + onMonthChange) and lock the
  // reaction to `index` until the scroll reaches it. Used for arrow navigation,
  // where the title should reflect the destination immediately while the scroll
  // animates there.
  const commitMonthIndex = (index: number) => {
    if (index < 0 || index >= months.length) return;
    previousMonthIndex.value = index;
    programmaticTargetIndex.value = index;
    handleMonthChange(index);
  };

  useAnimatedReaction(
    /* istanbul ignore next - worklet code runs on the UI thread */
    () => {
      if (!monthStride) return -1; // not measured yet — don't dispatch
      return Math.round(scrollOffset.value / monthStride);
    },
    /* istanbul ignore next - worklet code runs on the UI thread */
    (monthIndex = 0) => {
      // Width not measured yet — nothing to derive.
      if (monthIndex < 0) return;

      // A programmatic scroll is in flight: ignore intermediate frames and only
      // clear the lock once the live offset reaches the committed target.
      if (programmaticTargetIndex.value !== -1) {
        if (monthIndex === programmaticTargetIndex.value) {
          programmaticTargetIndex.value = -1;
        }
        return;
      }

      if (monthIndex !== previousMonthIndex.value) {
        previousMonthIndex.value = monthIndex;

        scheduleOnRN(handleMonthChange, monthIndex);
      }
    },
  );

  return {
    scrollViewRef,
    scrolledMonthIndex,
    scrollHandler,
    commitMonthIndex,
  };
};
